import type { Question } from '../../types';
import { HAB_MH_CONDITION } from './shared-options';

// ---------------------------------------------------------------------------
// Umbrella / Excess Liability Supplement
// Source: Centered Partners MHMF intake — "Umbrella & Excess" sheet.
// Habitational umbrella has tightened significantly; lender requirements
// often $5–25M. Gated on Umbrella selected + habitational / MH industry.
// ---------------------------------------------------------------------------

const UMB_CONDITION = [
  { field: 'coverage_lines', operator: 'contains' as const, value: 'umbrella' },
  ...HAB_MH_CONDITION,
];

export const umbrellaQuestions: Question[] = [
  {
    id: 'umb_limit_needed',
    text: 'What umbrella / excess limit do you need?',
    subtext: 'Lender requirements often range $5M–$25M for habitational portfolios.',
    section: 'coverage',
    group: 'umbrella',
    sort_order: 500,
    field_type: 'select',
    options: [
      { label: '$1M', value: '1000000' },
      { label: '$2M', value: '2000000' },
      { label: '$5M', value: '5000000' },
      { label: '$10M', value: '10000000' },
      { label: '$15M', value: '15000000' },
      { label: '$25M', value: '25000000' },
      { label: 'More than $25M', value: '25000000_plus' },
      { label: 'Not sure — advise me', value: 'advise' },
    ],
    required: true,
    field_path: 'umbrella.limit_needed',
    conditions: UMB_CONDITION,
    tags: ['umbrella', 'lob'],
  },
  {
    id: 'umb_underlying',
    text: 'Schedule of underlying coverage',
    subtext:
      'The limits and carriers beneath the umbrella — GL, Auto, EPLI, WC employer’s liability.',
    section: 'coverage',
    group: 'umbrella',
    sort_order: 510,
    field_type: 'repeating_group',
    required: true,
    field_path: 'umbrella.underlying',
    conditions: UMB_CONDITION,
    repeating_group: {
      min_rows: 1,
      max_rows: 10,
      add_button_label: 'Add another underlying line',
      row_label: 'Underlying line',
      fields: [
        {
          id: 'umb_u_line',
          text: 'Line of coverage',
          section: 'coverage',
          sort_order: 10,
          field_type: 'select',
          options: [
            { label: 'General Liability', value: 'general_liability' },
            { label: 'Commercial Auto', value: 'auto' },
            { label: 'EPLI', value: 'epli' },
            { label: "WC Employer's Liability", value: 'wc_el' },
            { label: 'Other', value: 'other' },
          ],
          required: true,
          field_path: 'line',
          conditions: [],
        },
        {
          id: 'umb_u_carrier',
          text: 'Carrier',
          section: 'coverage',
          sort_order: 20,
          field_type: 'text',
          placeholder: 'Travelers',
          required: true,
          field_path: 'carrier',
          conditions: [],
        },
        {
          id: 'umb_u_limit',
          text: 'Limit',
          section: 'coverage',
          sort_order: 30,
          field_type: 'currency',
          placeholder: '1000000',
          required: true,
          field_path: 'limit',
          conditions: [],
        },
      ],
    },
    tags: ['umbrella', 'lob', 'repeating'],
  },
  {
    id: 'umb_largest_tiv',
    text: 'Largest single property by total insured value (TIV)',
    subtext: 'Helps benchmark umbrella adequacy.',
    section: 'coverage',
    group: 'umbrella',
    sort_order: 520,
    field_type: 'currency',
    placeholder: '12000000',
    required: true,
    field_path: 'umbrella.largest_tiv',
    conditions: UMB_CONDITION,
    tags: ['umbrella', 'lob'],
  },
  {
    id: 'umb_cat_zones',
    text: 'Are any properties in catastrophe-exposed zones?',
    subtext: 'Select all that apply.',
    section: 'coverage',
    group: 'umbrella',
    sort_order: 530,
    field_type: 'multiselect',
    options: [
      { label: 'Wind', value: 'wind' },
      { label: 'Hail', value: 'hail' },
      { label: 'Flood', value: 'flood' },
      { label: 'Earthquake', value: 'earthquake' },
      { label: 'Wildfire', value: 'wildfire' },
      { label: 'None of these', value: 'none' },
    ],
    required: true,
    field_path: 'umbrella.cat_zones',
    conditions: UMB_CONDITION,
    tags: ['umbrella', 'lob'],
  },
  {
    id: 'umb_prior_cat',
    text: 'Any catastrophe events in the last 5 years?',
    section: 'coverage',
    group: 'umbrella',
    sort_order: 540,
    field_type: 'boolean',
    required: true,
    field_path: 'umbrella.prior_cat',
    conditions: UMB_CONDITION,
    tags: ['umbrella', 'lob'],
  },
  {
    id: 'umb_prior_cat_detail',
    text: 'Describe the catastrophe events',
    subtext: 'Losses paid and current open reserves.',
    section: 'coverage',
    group: 'umbrella',
    sort_order: 550,
    field_type: 'textarea',
    required: true,
    field_path: 'umbrella.prior_cat_detail',
    conditions: [...UMB_CONDITION, { field: 'umb_prior_cat', operator: 'eq', value: true }],
    tags: ['umbrella', 'lob'],
  },
];
